import { defer, EMPTY, Observable } from 'rxjs';
/**
 * Informing application-user about the status of user issued operations, such as success or failure.
 * Popping up an info or a warning message. Clearing displayed notification(s).
 *
 * Also, providing functions to parse errors (translate them) and also to handle them completely by trying to translate them and show the notification.
 */
import { Injectable, TemplateRef } from '@angular/core';
import { TOAST_DEFAULT_TIMEOUT } from '@app/app.constants';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { ToastAction, ToastService } from '@app/portal/services/toast-service';
import { ErrorDialogComponent } from '@app/shared/components/error-dialog/error-dialog.component';
import { DialogService } from '@app/shared/dialog/dialog.service';
import { ErrorTransformerService } from '@app/shared/services/error-transformer.service';
import { Translatable } from '@app/translate/models/translatable';
import { PortalTranslateService } from '@app/translate/services/portal-translate.service';
import { catchError, finalize, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  constructor(
    authenticationService: AuthenticationService,
    private dialogService: DialogService,
    private errorTransformerService: ErrorTransformerService,
    private portalTranslateService: PortalTranslateService,
    private toastService: ToastService
  ) {
    authenticationService.isAuthenticated$.subscribe((isAuthenticated) => {
      if (isAuthenticated === false) {
        this.toastService.clearFixed();
      }
    });
  }

  /**
   * Remove all or a single app-notification by id
   */
  clear(id?: AppNotificationId): void {
    if (id != null) {
      this.toastService.remove(id);
    } else {
      this.toastService.clear();
    }
  }

  /**
   * Will parse the error (try to translate the error (HTTP response)) and show the notification for it.
   * @param err to handle
   * @returns an observable that does not emit any value and immediately completes.
   */
  handleError = (err: any): Observable<never> => {
    if (err == null) {
      return EMPTY;
    }

    this.showError(err);

    return EMPTY;
  };

  show(options: ShowToastOptions): AppNotificationId {
    if (options.hideOpenToasts) {
      this.clear();
    }

    const translation = this.portalTranslateService.translate(options.message);
    const activeToast = this.toastService.show({
      body: translation,
      delay: options.delay,
      hideOpenToasts: options.hideOpenToasts,
      showProgress: options.showProgress,
      type: options.type,
    });

    return activeToast.id;
  }

  /**
   * Displays a danger notification with display duration of 5s (toastr default).
   */
  showDanger(message: Translatable): AppNotificationId {
    return this.show({
      message,
      type: 'danger',
    });
  }

  showError(err: any, options?: ErrorNotificationOptions): void {
    let title: Translatable = options?.title ?? 'error';
    let message: Translatable;

    const description = this.errorTransformerService.describeError(err);

    if (description != null) {
      if (description.details != null) {
        title = description.details.resultCode ?? description.details.exception;
        message = description.details.hint ?? description.details.message;
      }

      title ??= description.title ?? description.details?.title ?? 'common.errors.title.single';
      message ??= description.message ?? description.details?.message;
    }

    const showDetailsAction: ToastAction = {
      invoke: (toast) => {
        this.dialogService.open(ErrorDialogComponent, { size: 'lg', windowClass: 'error-dialog' }).withInput({
          description: options?.description,
          errors: Array.isArray(err) ? err : [err],
          title: options?.dialogTitle ?? options?.title ?? title,
        });

        toast.hide();
      },
      label: options?.action ?? 'common.errors.toastAction',
      variant: 'danger',
    };

    this.toastService.show({
      actions: [showDetailsAction],
      delay: 0,
      body: options?.title != null ? title : message,
      header: options?.title ?? title,
      showProgress: false,
      stay: true,
      toastId: options?.toastId,
    });
  }

  /**
   * Displays an info notification with progress bar and display duration of 10s.
   */
  showInfo(message: Translatable): AppNotificationId {
    return this.show({
      message,
      delay: 10000,
      showProgress: true,
      type: 'info',
    });
  }

  /**
   * Show an info notification that remains open until the given observable completes.
   *
   * @param observable Observable to watch
   * @param name i18n key that is translated and passed to the inProgress translation (as the i18n param name)
   * @param inProgressKey i18n key for the message that is shown, defaults to common.update.inProgress
   */
  showInfoWhileRunning<T>(
    observable: Observable<T>,
    name: string,
    inProgressKey: string = 'common.update.inProgress'
  ): Observable<T> {
    const toastId = this.show({
      delay: 0,
      hideOpenToasts: true,
      message: {
        key: inProgressKey,
        parameters: {
          name: this.portalTranslateService.translate(name),
        },
      },
      type: 'info',
    });

    return observable.pipe(
      finalize(() => {
        this.clear(toastId);
      })
    );
  }

  /**
   * Show a notification until the observable is completed. Switches the
   * notification message to the {@link success} when the observable completes.
   * @param observable The observable to watch.
   * @param loading This message is displayed while the observable is not yet complete.
   * @param error This is the message that is displayed when the observable fails.
   * @param success This is the message that is displayed when the observable completes.
   */
  showLoadingToast<T>(
    observable: Observable<T>,
    { loading, error, success }: LoadingToastOptions
  ): Observable<T> {
    return defer(() => {
      const toastId = this.show({
        delay: 0,
        hideOpenToasts: true,
        message: loading,
        showProgress: true,
        type: 'info',
      });

      return observable.pipe(
        tap({
          complete: () => {
            this.toastService.updateToast(toastId, {
              body: success ?? loading,
              delay: TOAST_DEFAULT_TIMEOUT,
              hideOnClick: true,
              showProgress: false,
              type: 'success',
            });
          },
        }),
        catchError((err) => {
          this.showError(err, {
            title: error,
            toastId,
          });

          return EMPTY;
        })
      );
    });
  }
  /**
   * Displays a success notification with display duration of 5s (toastr default).
   */
  showSuccess(message: Translatable): AppNotificationId {
    return this.toastService.success(this.portalTranslateService.translate(message)).id;
  }

  showSuccessfulCreate(translationKey: string): AppNotificationId {
    return this.showSuccess({
      key: 'common.create.successful',
      parameters: {
        name: this.portalTranslateService.translate(translationKey),
      },
    });
  }

  showSuccessfulDelete(translationKey: string): AppNotificationId {
    return this.showSuccess({
      key: 'common.delete.successful',
      parameters: {
        name: this.portalTranslateService.translate(translationKey),
      },
    });
  }

  showSuccessfulSave(translationKey: string): AppNotificationId {
    return this.showSuccess({
      key: 'common.update.successful',
      parameters: {
        name: this.portalTranslateService.translate(translationKey),
      },
    });
  }

  /**
   * Displays a warning notification with display duration of 5s.
   */
  showWarning(message: Translatable): AppNotificationId {
    return this.show({
      message,
      type: 'warning',
    });
  }

  /**
   * Show a notification until the observable is completed.
   */
  showWhileRunning<T>(observable: Observable<T>, message: Translatable): Observable<T> {
    return defer(() => {
      const toastId = this.show({
        delay: 0,
        hideOpenToasts: true,
        message,
        showProgress: true,
        type: 'info',
      });

      return observable.pipe(
        finalize(() => {
          this.clear(toastId);
        })
      );
    });
  }
}

/** Notification ID. Use this to close it individually */
export type AppNotificationId = string;

interface ShowToastOptions {
  /**
   * Delay in ms until the toast is hidden.
   * Set to 0 to disable automatically hiding the toast.
   */
  delay?: number;

  /**
   * If currently open toasts should be hidden before showing this toast.
   */
  hideOpenToasts?: boolean;

  /**
   * i18n key or a {@link Translation}
   */
  message: Translatable;

  /**
   * Show a progress indicator. Type depends on the delay:
   *  - progress bar if the delay is > 0
   *  - spinner if the delay is 0
   */
  showProgress?: boolean;

  /**
   * Toast type
   */
  type: 'success' | 'warning' | 'info' | 'danger';
}

interface ErrorNotificationOptions {
  /**
   * Used as the label of the toast action to open the error dialog.
   */
  action?: Translatable;

  /**
   * Passed to the error dialog as the description, which is shown above the error list.
   * If the description is a {@link TemplateRef}, the errors can be used with `let-errors="errors"`.
   */
  description?: Translatable | TemplateRef<any>;

  /**
   * Title of the error dialog. Defaults to {@link title}.
   */
  dialogTitle?: Translatable;

  /**
   * If a toastId is defined then this error toast will replace the toast.
   */
  toastId?: string;

  /**
   * Title of the toast.
   */
  title?: Translatable;
}

interface LoadingToastOptions {
  error?: Translatable;
  loading: Translatable;
  success?: Translatable;
}
